// Central per-page SEO catalog. Every public route should have an entry
// here so titles/descriptions stay unique (Semrush flagged duplicates).
// Long-form `content` blocks feed <GameSEOContent /> and add real text
// for Google to rank against (previous text-to-HTML ratio was ~1%).

const ORIGIN = "https://zombiecollector3d.com";
const OG = `${ORIGIN}/og-image.png`;

export const SEO_HOME = {
  title: "Zombie Arcade — Free Online Zombie Games | No Download",
  description:
    "Play free zombie games online — Zombie Collector 3D, Zombie Solitaire and more. One login, shared coins, VIP perks. No downloads, no install, instant play.",
  keywords:
    "zombie games, free zombie games, free online games, browser games no download, zombie arcade, html5 games, free games to play",
  canonical: `${ORIGIN}/`,
  ogImage: OG,
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Zombie Arcade",
    url: ORIGIN,
    description:
      "Free zombie games in your browser — 3D arcade shooter, classic Klondike solitaire, match-3 and more. Shared coin economy and VIP rewards across all titles.",
  },
};

export const SEO_GAMES = {
  "zombie-collector-3d": {
    title:
      "Zombie Collector 3D — Free 3D UFO Beam-the-Outbreak Game",
    description:
      "Pilot a UFO, beam up the zombie horde, survive 11 themed worlds. Free 3D browser game with endless mode, roguelike upgrades, cosmetic shop and global leaderboard.",
    keywords:
      "zombie collector 3d, 3d zombie game, ufo beam game, free 3d browser game, endless zombie game, three.js zombie game",
    canonical: `${ORIGIN}/games/zombie-collector-3d`,
    ogImage: OG,
    content: {
      tagline: "Pilot a UFO, beam up the outbreak.",
      overview:
        "Zombie Collector 3D is a free 3D browser arcade game where you pilot an alien tractor-beam over an unfolding zombie apocalypse. Drift across rooftops, cornfields, frozen tundras and 11 unique worlds — each with its own zombie variants and bosses. Every wave you survive feeds into a roguelike upgrade loop: spend coins on talents, cosmetics and new beam colors, then push deeper into endless mode against scaling hordes.",
      howToPlay: [
        "Move your UFO with WASD or the on-screen joystick.",
        "Hold the trigger / spacebar to activate your tractor beam — anything in range gets vacuumed up.",
        "Each zombie collected earns coins and feeds your meter; full meter unlocks a wave-clearing pulse.",
        "Survive bosses at the end of each world to unlock the next biome.",
        "After clearing all 11 worlds you enter endless mode — every wrap-around scales difficulty and rewards.",
      ],
      features: [
        "11 unique themed worlds with distinct zombie variants and bosses",
        "Endless mode with infinite scaling waves",
        "Roguelike talent + cosmetic upgrade system",
        "Earned-coin Cosmetic Shop — UFO shapes and beam colors",
        "Optional VIP / Founder tiers with carry-over perks between runs",
        "Global leaderboard and shared coin economy across the Zombie Arcade",
      ],
      faqs: [
        {
          q: "Is Zombie Collector 3D free to play?",
          a: "Yes. The full game is free in your browser — no install, no signup required. Optional VIP and Founder tiers add cosmetic perks and quality-of-life bonuses but the core game stays free forever.",
        },
        {
          q: "Does it work on mobile?",
          a: "Yes. The game runs in Chrome, Safari and Firefox on phones and tablets with an on-screen joystick and tap-to-beam controls.",
        },
        {
          q: "Do I need to download anything?",
          a: "No. Zombie Collector 3D runs entirely in your browser using Three.js and React Three Fiber. Open the page and play.",
        },
      ],
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      name: "Zombie Collector 3D",
      url: `${ORIGIN}/games/zombie-collector-3d`,
      genre: ["Arcade", "Action", "3D"],
      gamePlatform: ["Web Browser", "HTML5"],
      operatingSystem: "Any (Browser)",
      applicationCategory: "Game",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  },

  "zombie-solitaire": {
    title:
      "Zombie Solitaire — Free Klondike Card Game with Drag & Drop",
    description:
      "Play free Klondike Solitaire reimagined with zombie-themed cards. Drag-and-drop, auto-finish, VIP undo and hint perks. Earn coins on every win. No downloads.",
    keywords:
      "zombie solitaire, free solitaire online, klondike solitaire, free card games, solitaire no download, themed solitaire",
    canonical: `${ORIGIN}/games/zombie-solitaire`,
    ogImage: OG,
    content: {
      tagline: "Klondike Solitaire with a graveyard twist.",
      overview:
        "Zombie Solitaire is a free Klondike Solitaire game with a hand-drawn zombie deck — Brains, Blood, Tombstones and Bones instead of Hearts, Diamonds, Spades and Clubs. Drag-and-drop or tap to move cards, auto-finish kicks in the moment the game is mathematically won, and every win pays out coins you can spend across the Zombie Arcade. VIP players get unlimited undos, in-game hints and a forgiving stack-recovery rule that makes losing streaks far rarer.",
      howToPlay: [
        "Click or tap a face-up card to auto-route it to its rightful foundation or tableau column.",
        "Press and drag to manually move a card or a valid alternating-color sub-stack.",
        "Build foundations from Ace through King in each of the four zombie suits to win.",
        "Click the stock pile (face-down deck) to draw — Draw 1 for easy mode, Draw 3 for classic.",
        "Use Undo if you make a mistake, Hint if you get stuck. Non-VIP players get 3 free undos.",
      ],
      features: [
        "Hand-drawn zombie suit theme: Brains, Blood, Tombstones, Bones",
        "Custom zombie skull card-back design",
        "Drag-and-drop with forgiving auto-route fallback",
        "Auto-finish once every card is face-up",
        "Draw 1 and Draw 3 modes",
        "VIP / Founder perks: unlimited undos and hints",
        "Coin rewards that work across every game in the Zombie Arcade",
      ],
      faqs: [
        {
          q: "Is Zombie Solitaire the same as classic Klondike?",
          a: "Yes — the rules are exactly classic Klondike Solitaire (Patience). The only difference is the cosmetic suit theme: Brains and Blood replace Hearts and Diamonds, Tombstones and Bones replace Spades and Clubs.",
        },
        {
          q: "Can I play on my phone?",
          a: "Absolutely. The drag-and-drop is built on pointer events so it works identically on touchscreens and desktops.",
        },
        {
          q: "How do I earn coins?",
          a: "Every win awards coins based on your move count and time. VIP and Founder players earn bonus coins on top.",
        },
      ],
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      name: "Zombie Solitaire",
      url: `${ORIGIN}/games/zombie-solitaire`,
      genre: ["Card Game", "Solitaire", "Klondike"],
      gamePlatform: ["Web Browser", "HTML5"],
      operatingSystem: "Any (Browser)",
      applicationCategory: "Game",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  },

  "zombie-crush": {
    title: "Zombie Crush — Free Match-3 Zombie Puzzle Game",
    description:
      "Play Zombie Crush — a free match-3 puzzle with zombies, mummies and pumpkin heads. Swap, match and trigger cascade combos to clear each level. Free, no downloads.",
    keywords:
      "zombie crush, match 3 zombie game, free match 3 online, zombie puzzle, free puzzle game online",
    canonical: `${ORIGIN}/games/zombie-crush`,
    ogImage: OG,
    content: {
      tagline: "Swap, match three, cascade.",
      overview:
        "Zombie Crush is a free match-3 puzzle game with 6 zombie-apocalypse objects on a 7×7 board — fresh Brains, weathered Skulls, glowing Toxic Flasks, twisting DNA Helixes, slabs of Rotten Meat and crumbling Tombstones. Swap any two adjacent tiles to make a row or column of three or more matching objects and they vanish in a satisfying pop. Tiles above fall into the gaps and new ones spill in from the top, often triggering cascade chains for huge bonus scores. Beat the score goal before you run out of moves to clear the level.",
      howToPlay: [
        "Tap a tile to select it, then tap an adjacent tile to swap them.",
        "Or press-and-drag a tile toward its neighbor for a quick swipe-swap.",
        "Any swap that creates a row or column of 3+ matching tiles clears them.",
        "Falling tiles can trigger cascade matches — chain combos earn bonus score.",
        "Reach the level's score goal before your moves run out to clear it and unlock the next level (harder goal).",
      ],
      features: [
        "6 distinct zombie-themed match tiles — Brain, Skull, Flask, DNA, Meat, Tombstone",
        "Smooth cascade-match scoring with combo multipliers",
        "Auto-shuffle when no moves are possible (no dead boards)",
        "Endless level progression with scaling goals",
        "Coin rewards for level clears, paid into the Zombie Arcade wallet",
        "Local personal-best tracking even without an account",
      ],
      faqs: [
        {
          q: "Is Zombie Crush free?",
          a: "Yes — the full game is free in your browser. No download, no install, no signup. Sign in with Google to bank coins for level clears.",
        },
        {
          q: "How are levels generated?",
          a: "Each level uses the same 10 zombie tile pool, with the score goal scaling roughly 35% per level. After ~10 levels you're chasing 6-figure scores.",
        },
        {
          q: "What happens when no moves are possible?",
          a: "Zombie Crush auto-shuffles the board instead of forcing you into a dead position — you'll see a quick \"No moves left — shuffling!\" message.",
        },
      ],
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      name: "Zombie Crush",
      url: `${ORIGIN}/games/zombie-crush`,
      genre: ["Puzzle", "Match 3"],
      gamePlatform: ["Web Browser", "HTML5"],
      operatingSystem: "Any (Browser)",
      applicationCategory: "Game",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  },

  "zombie-merge": {
    title: "Zombie Merge — Free 2048-Style Zombie Puzzle Game",
    description:
      "Play Zombie Merge — a 2048-style sliding puzzle where identical zombies merge into stronger horrors. Reach the Zombie King to win. Free, browser-based, no downloads.",
    keywords:
      "zombie merge, zombie 2048, merge puzzle game, free merge game, zombie evolution game, 2048 online free",
    canonical: `${ORIGIN}/games/zombie-merge`,
    ogImage: OG,
    content: {
      tagline: "Drag, match, evolve. Survive the grid.",
      overview:
        "Zombie Merge is a free drag-to-merge puzzle game. Pick up any zombie tile and drag it onto a matching tile to fuse them into the next evolution tier. Empty cells let you reorganise freely. Start with shambling hands, evolve through skulls, ghouls, mummies, vampires, wights, liches, wraiths, and ultimately the legendary Zombie King at the 2048 tier. Each merge scores coins for the shared Zombie Arcade economy. Unlike a sliding 2048, the rest of the board stays put — only the tile you drag moves, so you control every play with surgical precision.",
      howToPlay: [
        "Press and hold any zombie tile, then drag it across the 4×4 board.",
        "Drop it on a tile of the same value to merge them into the next tier.",
        "Drop it on an empty cell to relocate it without merging.",
        "Each action spawns a new tile (mostly value 2, occasionally 4) somewhere on the board.",
        "Reach the Zombie King (2048) to win. You can keep going for higher tiers afterwards.",
      ],
      features: [
        "Classic drag-to-merge mechanic — no whole-board sliding",
        "11+ zombie evolution tiers from Hand through Zombie King and beyond",
        "Smooth slide animations and merge pulse effects",
        "Valid drop targets highlighted while dragging",
        "Coin rewards that scale with your highest tile, paid into the Zombie Arcade wallet",
        "Local personal-best tracking even without an account",
      ],
      faqs: [
        {
          q: "Is Zombie Merge free to play?",
          a: "Yes — the full game is free in your browser. No download, no installation, no signup required. Sign in with Google to earn coins for your runs.",
        },
        {
          q: "How is this different from 2048?",
          a: "In 2048 every arrow press slides every tile on the board. In Zombie Merge you pick up ONE tile and drop it where you want. Everything else stays in place. Far more strategic control.",
        },
        {
          q: "Does it work on phones?",
          a: "Yes. The drag mechanic uses pointer events so it works identically on touchscreens and desktops.",
        },
      ],
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      name: "Zombie Merge",
      url: `${ORIGIN}/games/zombie-merge`,
      genre: ["Puzzle", "Sliding Puzzle", "2048"],
      gamePlatform: ["Web Browser", "HTML5"],
      operatingSystem: "Any (Browser)",
      applicationCategory: "Game",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  },

  "zombie-tycoon": {
    title: "Zombie Idle Tycoon — Free Browser Idle / Clicker Game",
    description:
      "Build a zombie empire — huts, labs, mines, reactors and 10 more facilities. Idle gold piles up while you're offline. Free, browser-based, no downloads.",
    keywords:
      "zombie tycoon, zombie idle, idle tycoon game, free idle game, browser tycoon, clicker zombie game, zombie management game",
    canonical: `${ORIGIN}/games/zombie-tycoon`,
    ogImage: OG,
    content: {
      tagline: "Run the undead economy. Even when you're not here.",
      overview:
        "Zombie Idle Tycoon is a free browser-based idle/clicker game where you build, upgrade, and manage 14 distinct zombie facilities — from a humble Zombie Hut to the world-ending Apocalypse Core. Every building generates gold per second on its own, and you can tap any active building for an instant +1.5-second bonus. Gold compounds into upgrades, which compound into a bigger income rate, which unlocks the next tier of buildings. Step away and your zombie workforce keeps earning — up to 4 hours of offline production at 50% efficiency. Hitting lifetime-gold milestones pays out Arcade Coins you can spend across the rest of the Zombie Arcade.",
      howToPlay: [
        "Start with a free Zombie Hut — it generates gold by itself.",
        "Tap your built buildings for a quick instant gold bonus.",
        "Spend gold to upgrade buildings (each level boosts income by 10%).",
        "Earn enough lifetime gold to unlock the next building, then build it.",
        "Close the tab and come back later — offline gold keeps stacking up.",
      ],
      features: [
        "14 zombie-themed buildings with unique art",
        "Idle income with offline catch-up (50% efficiency, capped at 4 hours)",
        "Tap-to-collect bonus loop on top of passive income",
        "Arcade-coin milestone payouts shared across every Zombie Arcade game",
        "Auto-saves to your browser — pick up exactly where you left off",
        "Designed mobile-first — touch-friendly cards, no fiddly controls",
      ],
      faqs: [
        {
          q: "Is Zombie Idle Tycoon free?",
          a: "100% free. No download, no install, no signup required. Sign in with Google to claim Arcade Coin milestone rewards.",
        },
        {
          q: "What is idle income?",
          a: "Every building you build generates gold per second automatically. You don't have to click — but tapping gives you an instant bonus equal to ~1.5 seconds of that building's income.",
        },
        {
          q: "Does it keep earning while I'm gone?",
          a: "Yes — when you return, you'll see a Welcome Back screen with the gold your zombies earned offline (capped at 4 hours, at half the live rate).",
        },
      ],
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      name: "Zombie Idle Tycoon",
      url: `${ORIGIN}/games/zombie-tycoon`,
      genre: ["Idle", "Clicker", "Tycoon", "Management"],
      gamePlatform: ["Web Browser", "HTML5"],
      operatingSystem: "Any (Browser)",
      applicationCategory: "Game",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  },

  "zombie-tower-defense": {
    title:
      "Zombie Tower Defense — Free Zombie TD Strategy Game (Coming Soon)",
    description:
      "Zombie Tower Defense: place towers, traps and upgrades to stop endless waves of zombies. Coming soon to Zombie Arcade. Free, browser-based, no downloads.",
    keywords:
      "zombie tower defense, free tower defense, zombie td, free td game, tower defense online",
    canonical: `${ORIGIN}/games/zombie-tower-defense`,
    ogImage: OG,
    content: {
      tagline: "Place towers. Stop the horde. Save the survivors.",
      overview:
        "Zombie Tower Defense is a tactical TD game coming soon to Zombie Arcade. Place a mix of marksmen, flamethrowers, traps and barricades along the path to your survivor camp. Spend earned coins between waves to upgrade towers, unlock new tower types and research permanent perks. Endless mode pits you against infinitely scaling zombie waves with elite mutations and bosses.",
      features: [
        "Classic path-based tower defense with zombie waves",
        "5+ tower types with branching upgrade trees",
        "Survivor-camp meta-progression between runs",
        "Endless mode with scaling boss waves",
        "Coin rewards shared with the rest of the Zombie Arcade",
      ],
      faqs: [
        {
          q: "When does Zombie Tower Defense launch?",
          a: "It's on the Zombie Arcade roadmap after the puzzle titles. Check back on the homepage for a live badge.",
        },
      ],
    },
  },
};

// Helper used by SEO components to look up by route.
export function seoForRoute(pathname) {
  if (pathname === "/" || pathname === "/arcade") return SEO_HOME;
  const m = pathname.match(/^\/games\/([^/]+)/);
  if (m && SEO_GAMES[m[1]]) return SEO_GAMES[m[1]];
  return null;
}
